'use client';

import { useState } from 'react';
import { Link } from 'react-router-dom';
import {
  useBadges,
  getBadgeTierStyles,
  getBadgeGroupName,
} from '../../hooks/use-badges';
import { useAuth } from '../../hooks/use-auth';
import { useUserBadges } from '../../hooks/use-badges';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from '@/components/ui/card';
import { Skeleton } from '@/components/ui/skeleton';
import { CheckCircle, Award, Users, ArrowLeft } from 'lucide-react';
import { cn } from '../../lib/utils';

/**
 * Badge Card Component
 */
function BadgeCard({ badge, earned = false, onClick }) {
  const tierStyles = getBadgeTierStyles(badge.tier);

  return (
    <Card
      className={cn(
        'relative overflow-hidden cursor-pointer transition-all duration-300 hover:scale-105',
        'bg-zinc-900/50 border-white/10 hover:border-white/20',
        earned && 'ring-2 ring-green-500/50'
      )}
      onClick={onClick}
    >
      <CardContent className="p-4">
        <div className="flex items-start gap-3">
          {/* Badge Icon */}
          <div
            className={cn(
              'w-12 h-12 rounded-xl flex items-center justify-center text-2xl shadow-lg',
              tierStyles.bg,
              `shadow-lg ${tierStyles.glow}`
            )}
          >
            {badge.imageUrl ? (
              <img
                src={badge.imageUrl}
                alt={badge.name}
                className="w-8 h-8 object-contain"
              />
            ) : (
              badge.icon
            )}
          </div>

          <div className="flex-1 min-w-0">
            <div className="flex items-center gap-2">
              <h3 className="font-semibold text-white truncate">
                {badge.name}
              </h3>
              {earned && (
                <CheckCircle className="h-4 w-4 text-green-500 flex-shrink-0" />
              )}
            </div>
            <p className="text-xs text-zinc-400 line-clamp-2 mt-1">
              {badge.description}
            </p>
          </div>
        </div>

        {/* Stats row */}
        <div className="flex items-center justify-between mt-3 pt-3 border-t border-white/5">
          <Badge
            variant="outline"
            className={cn(
              'text-[10px] capitalize',
              badge.tier === 'gold' && 'border-yellow-500/50 text-yellow-400',
              badge.tier === 'silver' && 'border-slate-400/50 text-slate-300',
              badge.tier === 'bronze' && 'border-orange-500/50 text-orange-400',
              badge.tier === 'special' && 'border-violet-500/50 text-violet-400'
            )}
          >
            {badge.tier}
          </Badge>
          <span className="text-[10px] text-zinc-500 flex items-center gap-1">
            <Users className="h-3 w-3" />
            {badge.grantCount || 0} earned
          </span>
        </div>
      </CardContent>
    </Card>
  );
}

/**
 * Badge Group Section
 */
function BadgeGroup({ groupName, badges, earnedSlugs, onBadgeClick }) {
  return (
    <div className="mb-8">
      <h2 className="text-lg font-semibold text-white mb-4 flex items-center gap-2">
        <Award className="h-5 w-5 text-violet-400" />
        {getBadgeGroupName(groupName)}
      </h2>
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-4">
        {badges.map((badge) => (
          <BadgeCard
            key={badge.slug}
            badge={badge}
            earned={earnedSlugs.has(badge.slug)}
            onClick={() => onBadgeClick(badge)}
          />
        ))}
      </div>
    </div>
  );
}

/**
 * Badge Detail Modal
 */
function BadgeDetailModal({ badge, onClose, earned }) {
  const tierStyles = getBadgeTierStyles(badge.tier);

  if (!badge) return null;

  return (
    <div
      className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/80 backdrop-blur-sm"
      onClick={onClose}
    >
      <div
        className="bg-zinc-900 border border-white/10 rounded-2xl max-w-md w-full p-6 shadow-2xl"
        onClick={(e) => e.stopPropagation()}
      >
        {/* Badge Icon */}
        <div className="flex justify-center mb-4">
          <div
            className={cn(
              'w-24 h-24 rounded-2xl flex items-center justify-center text-5xl shadow-2xl',
              tierStyles.bg,
              `shadow-2xl ${tierStyles.glow}`
            )}
          >
            {badge.imageUrl ? (
              <img
                src={badge.imageUrl}
                alt={badge.name}
                className="w-16 h-16 object-contain"
              />
            ) : (
              badge.icon
            )}
          </div>
        </div>

        {/* Badge Info */}
        <div className="text-center mb-4">
          <h2 className="text-2xl font-bold text-white mb-1">{badge.name}</h2>
          <Badge
            variant="outline"
            className={cn(
              'capitalize mb-3',
              badge.tier === 'gold' && 'border-yellow-500/50 text-yellow-400',
              badge.tier === 'silver' && 'border-slate-400/50 text-slate-300',
              badge.tier === 'bronze' && 'border-orange-500/50 text-orange-400',
              badge.tier === 'special' && 'border-violet-500/50 text-violet-400'
            )}
          >
            {badge.tier}
          </Badge>
          <p className="text-zinc-400 mt-2">{badge.description}</p>
          {badge.longDescription && (
            <p className="text-zinc-500 text-sm mt-2">
              {badge.longDescription}
            </p>
          )}
        </div>

        {/* Stats */}
        <div className="flex items-center justify-center gap-4 py-4 border-t border-b border-white/10">
          <div className="text-center">
            <div className="text-2xl font-bold text-white">
              {badge.grantCount || 0}
            </div>
            <div className="text-xs text-zinc-500">Users Earned</div>
          </div>
          {badge.allowTitle && (
            <div className="text-center">
              <div className="text-sm text-violet-400">
                ✓ Can be used as title
              </div>
            </div>
          )}
        </div>

        {/* Status */}
        <div className="mt-4 text-center">
          {earned ? (
            <div className="flex items-center justify-center gap-2 text-green-500">
              <CheckCircle className="h-5 w-5" />
              <span className="font-medium">You have earned this badge!</span>
            </div>
          ) : (
            <div className="text-zinc-500">
              You haven't earned this badge yet.
            </div>
          )}
        </div>

        {/* Close Button */}
        <Button variant="outline" className="w-full mt-4" onClick={onClose}>
          Close
        </Button>
      </div>
    </div>
  );
}

/**
 * Badges Page
 */
export default function BadgesPage() {
  const { badges, groupedBadges, isLoading } = useBadges();
  const { user } = useAuth();
  const { badges: userBadges } = useUserBadges(user?.creator);
  const [selectedBadge, setSelectedBadge] = useState(null);
  const [activeTab, setActiveTab] = useState('all');

  // Create a set of earned badge slugs for quick lookup
  const earnedSlugs = new Set(userBadges.map((ub) => ub.badgeSlug));

  // Group order for display
  const groupOrder = [
    'getting-started',
    'posting',
    'community',
    'blockchain',
    'anniversary',
    'trust-level',
    'special',
  ];

  // Filter badges by tab
  const filteredBadges =
    activeTab === 'earned'
      ? badges.filter((b) => earnedSlugs.has(b.slug))
      : activeTab === 'not-earned'
      ? badges.filter((b) => !earnedSlugs.has(b.slug))
      : badges;

  // Group filtered badges
  const filteredGrouped = filteredBadges.reduce((acc, badge) => {
    if (!acc[badge.group]) {
      acc[badge.group] = [];
    }
    acc[badge.group].push(badge);
    return acc;
  }, {});

  if (isLoading) {
    return (
      <div className="min-h-screen bg-black pt-20">
        <div className="max-w-7xl mx-auto px-4 py-8">
          <Skeleton className="h-8 w-48 mb-8" />
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-4">
            {[...Array(12)].map((_, i) => (
              <Skeleton key={i} className="h-32 rounded-xl" />
            ))}
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-black pt-20">
      <div className="max-w-7xl mx-auto px-4 py-8">
        {/* Header */}
        <div className="mb-8">
          <Link
            to="/forum"
            className="inline-flex items-center gap-2 text-zinc-400 hover:text-white mb-4 transition-colors"
          >
            <ArrowLeft className="h-4 w-4" />
            Back to Forum
          </Link>
          <h1 className="text-3xl font-bold text-white mb-2">Badges</h1>
          <p className="text-zinc-400">
            Earn badges by participating in the community. Badges recognize your
            contributions and achievements.
          </p>
        </div>

        {/* Stats */}
        {user && (
          <div className="grid grid-cols-2 sm:grid-cols-4 gap-4 mb-8">
            <Card className="bg-zinc-900/50 border-white/10">
              <CardContent className="p-4 text-center">
                <div className="text-3xl font-bold text-white">
                  {badges.length}
                </div>
                <div className="text-xs text-zinc-500">Total Badges</div>
              </CardContent>
            </Card>
            <Card className="bg-zinc-900/50 border-white/10">
              <CardContent className="p-4 text-center">
                <div className="text-3xl font-bold text-green-400">
                  {earnedSlugs.size}
                </div>
                <div className="text-xs text-zinc-500">Earned</div>
              </CardContent>
            </Card>
            <Card className="bg-zinc-900/50 border-white/10">
              <CardContent className="p-4 text-center">
                <div className="text-3xl font-bold text-zinc-400">
                  {badges.length - earnedSlugs.size}
                </div>
                <div className="text-xs text-zinc-500">Remaining</div>
              </CardContent>
            </Card>
            <Card className="bg-zinc-900/50 border-white/10">
              <CardContent className="p-4 text-center">
                <div className="text-3xl font-bold text-violet-400">
                  {Math.round((earnedSlugs.size / badges.length) * 100) || 0}%
                </div>
                <div className="text-xs text-zinc-500">Completion</div>
              </CardContent>
            </Card>
          </div>
        )}

        {/* Tabs */}
        <Tabs value={activeTab} onValueChange={setActiveTab} className="mb-8">
          <TabsList className="bg-zinc-900/50 border border-white/10">
            <TabsTrigger
              value="all"
              className="data-[state=active]:bg-violet-600"
            >
              All Badges
            </TabsTrigger>
            {user && (
              <>
                <TabsTrigger
                  value="earned"
                  className="data-[state=active]:bg-green-600"
                >
                  Earned ({earnedSlugs.size})
                </TabsTrigger>
                <TabsTrigger
                  value="not-earned"
                  className="data-[state=active]:bg-zinc-600"
                >
                  Not Earned ({badges.length - earnedSlugs.size})
                </TabsTrigger>
              </>
            )}
          </TabsList>
        </Tabs>

        {/* Badge Groups */}
        {groupOrder.map((groupName) => {
          const groupBadges = filteredGrouped[groupName];
          if (!groupBadges || groupBadges.length === 0) return null;

          return (
            <BadgeGroup
              key={groupName}
              groupName={groupName}
              badges={groupBadges}
              earnedSlugs={earnedSlugs}
              onBadgeClick={setSelectedBadge}
            />
          );
        })}

        {/* Empty State */}
        {filteredBadges.length === 0 && (
          <div className="text-center py-12">
            <Award className="h-16 w-16 text-zinc-700 mx-auto mb-4" />
            <h3 className="text-lg font-medium text-zinc-400">
              No badges found
            </h3>
            <p className="text-zinc-500 text-sm mt-1">
              {activeTab === 'earned'
                ? 'Start participating to earn badges!'
                : 'No badges in this category.'}
            </p>
          </div>
        )}
      </div>

      {/* Badge Detail Modal */}
      {selectedBadge && (
        <BadgeDetailModal
          badge={selectedBadge}
          earned={earnedSlugs.has(selectedBadge.slug)}
          onClose={() => setSelectedBadge(null)}
        />
      )}
    </div>
  );
}
